/* eslint-disable @typescript-eslint/no-unused-vars */

import {
  GridColumnVisibilityModel,
  GridSortModel,
} from '@spotgamma/x-data-grid-premium';
import { RecoilState } from 'recoil';
import { HiroTimeRange } from './hiro';
import { HiroTab } from 'config/hiro';
import { OptionType, ValueFormat } from './shared';
import { TapeFilterTab } from 'config/tape';

export const OPTION_FEED_IDX = {
  SYM: 0,
  TIME: 1,
  DELTA: 2,
  GAMMA: 3,
  VEGA: 4,
  STOCK_PRICE: 5,
  TNS_INDEX: 6,
  EXPIRATION: 7,
  STRIKE: 8,
  SIZE: 9,
  FLAG: 10,
  EXCHANGE_SALE_CONDITIONS: 11,
  PRICE: 12,
  BID: 13,
  ASK: 14,
  IVOL: 15,
  PREVOI: 16,
  DAILYVOL: 17,
  PREMIUM: 19,
};

export enum OptionsFeedColumnKey {
  Time = 'ts',
  Underlying = 'underlying',
  TradeSide = 'trade_side', // dynamically generated value
  Aggressor = 'aggressor',
  IsPut = 'is_put',
  Strike = 'strike',
  DailyVolCumsum = 'daily_vol_cumsum',
  PrevOi = 'prev_oi',
  Expiry = 'expiry',
  Size = 'size',
  Premium = 'premium',
  StockPrice = 'stock_price',
  Price = 'price',
  Flags = 'flags',
  Bid = 'bid',
  Ask = 'ask',
  IVol = 'ivol',
  Delta = 'delta',
  Gamma = 'gamma',
  Vega = 'vega',
  TnsIndex = 'tnsIndex',
  ExchangeSaleCondition = 'exchangeSaleCondition',
  IsBlock = 'is_block',
  IsCross = 'is_cross',
  IsSweep = 'is_sweep',
  IsSpread = 'is_spread',
}

export interface RawOptionFeedData {
  id: string;
  contractString: string;
  [OptionsFeedColumnKey.Underlying]: string;
  [OptionsFeedColumnKey.Time]: bigint;
  [OptionsFeedColumnKey.Delta]: number;
  [OptionsFeedColumnKey.Gamma]: number;
  [OptionsFeedColumnKey.Vega]: number;
  [OptionsFeedColumnKey.StockPrice]: number;
  [OptionsFeedColumnKey.DailyVolCumsum]: number;
  [OptionsFeedColumnKey.PrevOi]: number;
  [OptionsFeedColumnKey.Expiry]: bigint;
  [OptionsFeedColumnKey.Strike]: number;
  [OptionsFeedColumnKey.Size]: number;
  [OptionsFeedColumnKey.Flags]: OptionFlag;
  [OptionsFeedColumnKey.ExchangeSaleCondition]: string;
  [OptionsFeedColumnKey.Price]: number;
  [OptionsFeedColumnKey.Bid]: number;
  [OptionsFeedColumnKey.Ask]: number;
  [OptionsFeedColumnKey.IVol]: number;
  [OptionsFeedColumnKey.Aggressor]: Aggressor;
  [OptionsFeedColumnKey.IsPut]: boolean;
  [OptionsFeedColumnKey.TnsIndex]: bigint;
  // dynamic
  [OptionsFeedColumnKey.Premium]: number;
  [OptionsFeedColumnKey.TradeSide]: OptionTradeSide;
  [OptionsFeedColumnKey.IsBlock]?: boolean;
  [OptionsFeedColumnKey.IsSpread]?: boolean;
  [OptionsFeedColumnKey.IsSweep]?: boolean;
  [OptionsFeedColumnKey.IsCross]?: boolean;
}

export type OptionsFeedColumnVisibility = Record<OptionsFeedColumnKey, boolean>;

export type OptionsFeedColumnSizes = Record<OptionsFeedColumnKey, number>;

export enum OptionTransactionType {
  NEW = 'n',
  CORRECTION = 'r',
  CANCEL = 'c',
}

export const OptionTypeLabels: Record<OptionType, string> = {
  [OptionType.PUT]: 'Put',
  [OptionType.CALL]: 'Call',
};

export enum Aggressor {
  BUY = 'buy',
  SELL = 'sell',
  UNDETERMINED = 'n/a',
}

export const AggressorLabels: Record<Aggressor, string> = {
  [Aggressor.BUY]: 'Buy',
  [Aggressor.SELL]: 'Sell',
  [Aggressor.UNDETERMINED]: 'Undetermined',
};

export enum OptionSaleType {
  BLOCK = 'block',
  SPREAD = 'spread',
  SWEEP = 'sweep',
  CROSS = 'cross',
}

export const OptionSaleTypeLabels: Record<OptionSaleType, string> = {
  [OptionSaleType.BLOCK]: 'Block',
  [OptionSaleType.SPREAD]: 'Spread',
  [OptionSaleType.SWEEP]: 'Sweep',
  [OptionSaleType.CROSS]: 'Cross',
};

export enum OptionTradeSide {
  BB = 'bb',
  B = 'b',
  M = 'm',
  A = 'a',
  AA = 'aa',
}

export const OptionTradeSideLabels: Record<OptionTradeSide, string> = {
  [OptionTradeSide.BB]: 'Below Bid',
  [OptionTradeSide.B]: 'Bid',
  [OptionTradeSide.M]: 'Mid',
  [OptionTradeSide.A]: 'Ask',
  [OptionTradeSide.AA]: 'Above Ask',
};

export interface OptionFlag {
  type: OptionType;
  transactionType: OptionTransactionType;
  sideType: Aggressor;
  isNextExp: boolean;
  isRetail: boolean;
  isBlock: boolean;
  isSpread: boolean;
  isSweep: boolean;
  isCross: boolean;
}

// [
//     string, //  0: underlying
//     BigInt, //  1: time
//     number, //  2: delta_notional
//     number, //  3: gamma_notional
//     number, //  4: vega_notional
//     number, //  5: stock_price
//     number, //  6: tns_index
//     BigInt, //  7: expiry
//     number, //  8: strike
//     number, //  9: size
//     number, // 10: flags
//     string, // 11: exchange sale conditions
//     number, // 12: price
//     number, // 13: bid
//     number, // 14: ask
//     number, // 15: ivol
//   ];

// enum for the operators
export enum FilterOperator {
  LessThan = '<',
  LessThanOrEqual = '<=',
  Equal = '=',
  GreaterThan = '>',
  GreaterThanOrEqual = '>=',
  NotEqual = '!=',
  IsAnyOf = 'isAnyOf',
}

// Type mapping for value based on operator
export type FilterValueMap = {
  [FilterOperator.LessThan]: number | bigint | null;
  [FilterOperator.LessThanOrEqual]: number | bigint | null;
  [FilterOperator.Equal]: number | string | boolean | null;
  [FilterOperator.GreaterThan]: number | bigint | null;
  [FilterOperator.GreaterThanOrEqual]: number | bigint | null;
  [FilterOperator.NotEqual]: number | string | boolean | null;
  [FilterOperator.IsAnyOf]: string[] | number[] | bigint[] | null;
};

export interface FilterOption {
  value: string | boolean;
  label: string;
}

export interface ConjunctionFilterOption {
  title: string;
  options: FilterOption[];
}

export enum ConjunctionOp {
  And = 'and',
  Or = 'or',
}

export interface FilterItem<FO extends FilterOperator = FilterOperator> {
  /**
   * Must be unique.
   * Useful when the model contains several items.
   */
  id: string;
  /**
   * The column from which we want to filter the rows.
   */
  field: string;

  /**
   * The name of the operator we want to apply.
   */
  operator: FilterOperator;

  /**
   * The filtering value.
   * The type of the value depends on the operator.
   */
  value: FilterValueMap[FO];
}

export interface Conjunction {
  id: string;
  op: ConjunctionOp;
  filters: Filter[];
}

export type Filter = Conjunction | FilterItem;

export interface FilterConfig {
  id: string;
  name: string;
  value: Filter[];
  noSym: boolean;
  viewOnly?: boolean;
}

export type FilterPanelProps = {
  openState: RecoilState<boolean>;
  panelView?: 'default' | 'modal';
  noSym?: boolean;
  currentSym?: string;
  hiroTimeRange?: HiroTimeRange;
  hiroTnsChartTimeInSync?: boolean;
};

export type FilterValueFormat = 'currency' | 'percentage' | 'number';
export type TransactionSentiment = 'bullish' | 'bearish' | 'neutral';

export interface TopNameItem {
  icon?: JSX.Element;
  underlying: string;
  values: {
    label: string;
    displayValue: string;
    filterItems?: FilterItem[];
    color?: string;
  }[];
}

// flow summary types
export interface PCFlowSum {
  put: number | null;
  call: number | null;
  total: number;
  format?: ValueFormat;
}

export interface TnsFlowSummary {
  volume: PCFlowSum;
  premium: PCFlowSum;
  delta: PCFlowSum;
  gamma: PCFlowSum;
  vega: PCFlowSum;
}

export interface PCFlowSumColors {
  put: string; // color for puts
  call: string; // color for calls
  total: string; // color for total
}

export type FlowSumCategory = 'delta' | 'vega' | 'gamma' | 'volume' | 'premium';

// flow highlight types

export interface BasicHighlight {
  underlying: string;
  val: number;
}

export interface PremiumHighlight {
  underlying: string;
  premium: number;
  expiry: number;
  strike: number;
  is_put: boolean; // keeping snake case for backend consistency instead of redefining a mapping util
}

export interface MoverHighlight {
  underlying: string;
  last_close: number;
  close: number;
}

export interface TnsFlowHighlights {
  premium: PremiumHighlight[];
  volume: BasicHighlight[];
  gamma: BasicHighlight[];
  movers: MoverHighlight[];
}

export interface TnsSortedDataWindow {
  sortModel: GridSortModel;
  firstRow?: RawOptionFeedData;
  lastRow?: RawOptionFeedData;
}

// contract data

// unique keys for contract data
export enum OptionsFeedContractColumnKey {
  TotalPremium = 'total_premium',
  PrevOi = 'prev_oi',
  TotalVolume = 'total_volume',
  DeltaNotional = 'delta_notional',
  GammaNotional = 'gamma_notional',
  VegaNotional = 'vega_notional',
}

export interface RawOptionFeedContract {
  id: string;
  contractString: string;
  [OptionsFeedColumnKey.Underlying]: string;
  [OptionsFeedColumnKey.IsPut]: boolean;
  [OptionsFeedColumnKey.Strike]: number;
  [OptionsFeedColumnKey.Expiry]: string;
  [OptionsFeedContractColumnKey.TotalVolume]: number;
  [OptionsFeedContractColumnKey.TotalPremium]: number;
  [OptionsFeedContractColumnKey.DeltaNotional]: number;
  [OptionsFeedContractColumnKey.GammaNotional]: number;
  [OptionsFeedContractColumnKey.VegaNotional]: number;
}

export type HiroTapeView = HiroTab.FlowData | HiroTab.ContractData;

/** Snapshot shape for Canvas DB persistence */
export interface TapeCanvasStateSnapshot {
  filters?: Filter[];
  activeCustomFilter?: FilterConfig;
  columnVisibility?: GridColumnVisibilityModel;
  columnSortModel?: GridSortModel;
  columnOrder?: OptionsFeedColumnKey[];
  columnSizes?: OptionsFeedColumnSizes;
  filterActiveTab?: TapeFilterTab;
  flowLive?: boolean;
  filterPanelOpen?: boolean;
  activeWatchlistIds?: number[];
}
